import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/auth/AuthContext";
import HeaderBar from "@/auth/HeaderBar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    ShoppingBag,
    MapPin,
    Calendar,
    Phone,
    Mail,
    RotateCcw,
    Loader2,
    Pause,
    Play,
    X,
    Sparkles,
    Truck,
} from "lucide-react";

const STATUS_PILL = {
    pending: { label: "Pending payment", cls: "bg-mint text-forest" },
    confirmed: { label: "Confirmed", cls: "bg-forest text-white" },
    preparing: { label: "Preparing", cls: "bg-gold text-navy" },
    out_for_delivery: { label: "Out for delivery", cls: "bg-sage text-white" },
    delivered: { label: "Delivered ✓", cls: "bg-forest text-white" },
    canceled: { label: "Canceled", cls: "bg-burnt text-white" },
};

export default function AccountPage() {
    const { user, loading, refresh } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState(null);
    const [plans, setPlans] = useState(null);
    const [planBusy, setPlanBusy] = useState(false);
    const [savingProfile, setSavingProfile] = useState(false);
    const [form, setForm] = useState({ name: "", phone: "" });

    const authHeaders = useCallback(() => {
        const token = localStorage.getItem("session_token");
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, []);

    const loadOrders = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/me/orders`, {
                headers: authHeaders(),
            });
            setOrders(res.data.orders || []);
        } catch (e) {
            console.error(e);
            setOrders([]);
        }
    }, [authHeaders]);

    const loadPlans = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/me/plans`, {
                headers: authHeaders(),
            });
            setPlans(res.data.plans || []);
        } catch (e) {
            console.error(e);
            setPlans([]);
        }
    }, [authHeaders]);

    useEffect(() => {
        if (!loading && !user) {
            navigate("/", { replace: true });
        }
    }, [loading, user, navigate]);

    useEffect(() => {
        if (user) {
            setForm({ name: user.name || "", phone: user.phone || "" });
            loadOrders();
            loadPlans();
        }
    }, [user, loadOrders, loadPlans]);

    const saveProfile = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            await axios.patch(`${API}/auth/me`, form, { headers: authHeaders() });
            await refresh();
            toast.success("Profile updated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save");
        } finally {
            setSavingProfile(false);
        }
    };

    const updatePlanStatus = async (planId, status) => {
        setPlanBusy(true);
        try {
            await axios.patch(
                `${API}/me/plans/${planId}`,
                { status },
                { headers: authHeaders() }
            );
            await loadPlans();
            toast.success(
                status === "active"
                    ? "Plan resumed"
                    : status === "paused"
                      ? "Plan paused"
                      : "Plan canceled"
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't update plan");
        } finally {
            setPlanBusy(false);
        }
    };

    const orderThisWeek = (plan) => {
        try {
            const cart = {};
            plan.items.forEach((it) => {
                cart[it.meal_id] = (cart[it.meal_id] || 0) + it.quantity;
            });
            sessionStorage.setItem("reorder_cart", JSON.stringify(cart));
            sessionStorage.setItem("reorder_subscribe", "1");
            toast.success("Plan loaded! Confirm delivery & checkout →");
            navigate("/");
        } catch {
            toast.error("Couldn't load plan");
        }
    };

    const reorder = (order) => {
        try {
            const cart = {};
            order.items.forEach((it) => {
                cart[it.meal_id] = (cart[it.meal_id] || 0) + it.quantity;
            });
            sessionStorage.setItem("reorder_cart", JSON.stringify(cart));
            toast.success("Re-ordering — your cart is loaded!");
            navigate("/#menu");
        } catch (e) {
            toast.error("Couldn't reorder. Try again.");
        }
    };

    if (loading || !user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cream">
                <Loader2 className="animate-spin text-forest" size={32} />
            </div>
        );
    }

    const savedAddresses = new Map();
    (orders || []).forEach((o) => {
        const addr = o?.customer?.address;
        if (addr && !savedAddresses.has(addr)) savedAddresses.set(addr, o.created_at);
    });

    return (
        <div className="min-h-screen bg-cream pb-20">
            <div className="relative hero-gradient grain pb-24 pt-2">
                <HeaderBar dark onSignIn={() => {}} />
                <div className="max-w-5xl mx-auto px-6 sm:px-8 pt-36 sm:pt-32">
                    <p className="text-mint/80 tracking-[0.3em] text-xs font-bold uppercase">
                        Your account
                    </p>
                    <h1 className="mt-3 font-serif font-black text-white text-4xl sm:text-5xl">
                        Hey, {user.name?.split(" ")[0] || "friend"} 👋
                    </h1>
                    <p className="mt-3 text-white/80 max-w-xl">
                        Track your orders, manage your address, and re-order
                        last week's plate in one tap.
                    </p>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 sm:px-8 mt-6 lg:-mt-16 grid lg:grid-cols-3 gap-6">
                {/* Profile card */}
                <section
                    id="profile"
                    data-testid="account-profile-card"
                    className="bg-white rounded-[20px] p-6 shadow-[0_8px_30px_rgba(0,0,0,0.08)] lg:col-span-1"
                >
                    <div className="flex items-center gap-3">
                        {user.picture ? (
                            <img
                                src={user.picture}
                                alt={user.name}
                                className="w-14 h-14 rounded-full object-cover"
                            />
                        ) : (
                            <span className="w-14 h-14 rounded-full bg-forest text-white font-bold text-lg flex items-center justify-center">
                                {(user.name || "?")
                                    .split(" ")
                                    .slice(0, 2)
                                    .map((p) => p[0])
                                    .join("")
                                    .toUpperCase()}
                            </span>
                        )}
                        <div className="min-w-0">
                            <p className="font-bold text-navy truncate">
                                {user.name}
                            </p>
                            <p className="text-xs text-muted2 truncate flex items-center gap-1">
                                <Mail size={12} />
                                {user.email}
                            </p>
                            {user.auth_provider && (
                                <p className="text-[10px] mt-0.5 uppercase tracking-widest text-forest font-bold">
                                    via {user.auth_provider}
                                </p>
                            )}
                        </div>
                    </div>

                    <form onSubmit={saveProfile} className="mt-6 space-y-3">
                        <Field label="Name">
                            <input
                                data-testid="profile-name-input"
                                value={form.name}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        name: e.target.value,
                                    }))
                                }
                                className="w-full px-3 py-2 rounded-lg bg-cream border border-mint focus:border-forest outline-none"
                            />
                        </Field>
                        <Field label="Phone">
                            <input
                                data-testid="profile-phone-input"
                                value={form.phone}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        phone: e.target.value,
                                    }))
                                }
                                className="w-full px-3 py-2 rounded-lg bg-cream border border-mint focus:border-forest outline-none"
                            />
                        </Field>
                        <Button
                            data-testid="profile-save-btn"
                            type="submit"
                            disabled={savingProfile}
                            className="w-full rounded-full bg-forest hover:bg-sage text-white font-bold py-4"
                        >
                            {savingProfile ? "Saving…" : "Save profile"}
                        </Button>
                    </form>

                    {savedAddresses.size > 0 && (
                        <div className="mt-6 pt-5 border-t border-mint">
                            <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                                Saved Addresses
                            </p>
                            <ul className="mt-2 space-y-2">
                                {[...savedAddresses.keys()].slice(0, 3).map((addr) => (
                                    <li
                                        key={addr}
                                        className="flex items-start gap-2 text-sm text-navy"
                                    >
                                        <MapPin
                                            size={14}
                                            className="text-forest mt-1 flex-shrink-0"
                                        />
                                        <span>{addr}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>

                {/* Orders list */}
                <section
                    data-testid="account-orders-section"
                    className="lg:col-span-2"
                >
                    {/* Meal Plan section */}
                    {plans !== null && plans.filter((p) => p.status !== "canceled").length > 0 && (
                        <div className="mb-6">
                            <div className="flex items-center gap-2 mb-3">
                                <Sparkles size={18} className="text-gold" />
                                <h2 className="font-serif font-black text-xl sm:text-2xl text-navy">
                                    Your weekly plan
                                </h2>
                            </div>
                            <div className="space-y-4">
                                {plans
                                    .filter((p) => p.status !== "canceled")
                                    .map((p) => (
                                        <PlanCard
                                            key={p.plan_id}
                                            plan={p}
                                            busy={planBusy}
                                            onPause={() =>
                                                updatePlanStatus(p.plan_id, "paused")
                                            }
                                            onResume={() =>
                                                updatePlanStatus(p.plan_id, "active")
                                            }
                                            onCancel={() =>
                                                updatePlanStatus(p.plan_id, "canceled")
                                            }
                                            onOrderNow={() => orderThisWeek(p)}
                                        />
                                    ))}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between">
                        <h2 className="font-serif font-black text-2xl sm:text-3xl text-navy">
                            Your orders
                        </h2>
                        <Link
                            to="/#menu"
                            className="text-forest font-bold text-sm hover:underline"
                            data-testid="back-to-menu-link"
                        >
                            ← Back to menu
                        </Link>
                    </div>

                    {orders === null ? (
                        <div className="mt-6 flex items-center gap-2 text-muted2">
                            <Loader2 className="animate-spin" size={16} /> Loading…
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="mt-8 bg-white rounded-[20px] p-10 text-center shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                            <div className="text-5xl">🍽️</div>
                            <p className="mt-3 font-bold text-navy text-lg">
                                No orders yet
                            </p>
                            <p className="text-muted2 text-sm mt-1">
                                Pick a Caribbean dish and we'll get cooking.
                            </p>
                            <Link to="/#menu">
                                <Button
                                    data-testid="empty-orders-cta"
                                    className="mt-5 rounded-full bg-gold hover:bg-goldDark text-navy font-bold px-6 py-5"
                                >
                                    See this week's menu
                                </Button>
                            </Link>
                        </div>
                    ) : (
                        <ul className="mt-5 space-y-4">
                            {orders.map((o) => (
                                <OrderCard
                                    key={o.order_id}
                                    order={o}
                                    onReorder={() => reorder(o)}
                                />
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            <div className="mt-20">
                <Footer />
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                {label}
            </span>
            <div className="mt-1">{children}</div>
        </label>
    );
}

function PlanCard({ plan, busy, onPause, onResume, onCancel, onOrderNow }) {
    const active = plan.status === "active";
    const lastDate = plan.last_ordered_at
        ? new Date(plan.last_ordered_at).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
          })
        : null;
    return (
        <div
            data-testid={`plan-card-${plan.plan_id}`}
            className="relative bg-white rounded-[20px] p-5 sm:p-6 shadow-[0_8px_30px_rgba(0,0,0,0.08)] overflow-hidden"
        >
            <div className="absolute -right-3 -top-3 w-24 h-24 rounded-full bg-gold/15" />
            <div className="absolute -left-4 -bottom-6 w-20 h-20 rounded-full bg-mint/60" />
            <div className="relative">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <Truck size={14} className="text-forest" />
                            <p className="text-[11px] uppercase tracking-widest font-bold text-forest">
                                Weekly plan · Subscribe & save 10%
                            </p>
                        </div>
                        <p className="font-serif font-black text-navy text-2xl mt-1">
                            ${plan.weekly_total.toFixed(2)}
                            <span className="text-base text-muted2 font-sans font-normal">
                                {" "}
                                / week
                            </span>
                        </p>
                        <p className="text-xs text-muted2 mt-0.5">
                            You save{" "}
                            <span className="text-forest font-bold">
                                ${plan.weekly_discount.toFixed(2)}
                            </span>{" "}
                            every delivery
                            {lastDate && (
                                <>
                                    {" "}
                                    · last ordered{" "}
                                    <span className="text-navy font-bold">
                                        {lastDate}
                                    </span>
                                </>
                            )}
                        </p>
                    </div>
                    <span
                        className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${active ? "bg-forest text-white" : "bg-cream text-muted2 border border-mint"}`}
                    >
                        {active ? "Active" : plan.status}
                    </span>
                </div>

                <ul className="mt-4 space-y-1 text-sm">
                    {plan.items.map((it, i) => (
                        <li
                            key={i}
                            className="flex justify-between text-navy"
                        >
                            <span className="truncate pr-2">
                                {it.quantity}× {it.name}
                            </span>
                            <span className="font-bold tabular-nums text-muted2">
                                ${it.line_total.toFixed(2)}
                            </span>
                        </li>
                    ))}
                </ul>

                {plan.delivery_address && (
                    <p className="mt-3 text-xs text-muted2 flex items-start gap-1.5">
                        <MapPin
                            size={12}
                            className="text-forest mt-0.5 flex-shrink-0"
                        />
                        {plan.delivery_address}
                    </p>
                )}

                <p className="mt-3 text-[11px] text-muted2 italic leading-relaxed">
                    📦 Heads-up: we'll roll out auto-billing soon. For now,
                    tap <span className="font-bold text-navy">Order this week's box</span>{" "}
                    every week — your 10% discount is applied automatically.
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                        data-testid={`plan-order-now-${plan.plan_id}`}
                        onClick={onOrderNow}
                        disabled={!active || busy}
                        className="rounded-full bg-gold hover:bg-goldDark text-navy font-bold py-2 h-auto px-5 text-sm flex items-center gap-1.5"
                    >
                        <RotateCcw size={14} /> Order this week's box
                    </Button>
                    {active ? (
                        <Button
                            data-testid={`plan-pause-${plan.plan_id}`}
                            variant="ghost"
                            onClick={onPause}
                            disabled={busy}
                            className="rounded-full text-navy hover:bg-mint h-auto py-2 px-4 text-sm flex items-center gap-1.5"
                        >
                            <Pause size={14} /> Pause
                        </Button>
                    ) : (
                        <Button
                            data-testid={`plan-resume-${plan.plan_id}`}
                            variant="ghost"
                            onClick={onResume}
                            disabled={busy}
                            className="rounded-full text-navy hover:bg-mint h-auto py-2 px-4 text-sm flex items-center gap-1.5"
                        >
                            <Play size={14} /> Resume
                        </Button>
                    )}
                    <Button
                        data-testid={`plan-cancel-${plan.plan_id}`}
                        variant="ghost"
                        onClick={onCancel}
                        disabled={busy}
                        className="rounded-full text-burnt hover:bg-cream h-auto py-2 px-4 text-sm flex items-center gap-1.5"
                    >
                        <X size={14} /> Cancel plan
                    </Button>
                </div>
            </div>
        </div>
    );
}

function OrderCard({ order, onReorder }) {
    const pill = STATUS_PILL[order.status] || STATUS_PILL.pending;
    const date = order.created_at
        ? new Date(order.created_at).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
          })
        : "";
    return (
        <li
            data-testid={`order-card-${order.order_id}`}
            className="bg-white rounded-[20px] p-5 sm:p-6 shadow-[0_6px_24px_rgba(0,0,0,0.07)]"
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                        Order · {date}
                    </p>
                    <p className="font-mono text-xs text-muted2 mt-0.5">
                        {order.order_id.slice(0, 8)}…
                    </p>
                </div>
                <span
                    className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${pill.cls}`}
                >
                    {pill.label}
                </span>
            </div>

            <ul className="mt-4 space-y-1 text-sm">
                {order.items.map((it, i) => (
                    <li key={i} className="flex justify-between text-navy">
                        <span className="truncate pr-2">
                            {it.quantity}× {it.name}
                        </span>
                        <span className="font-bold tabular-nums">
                            ${it.line_total.toFixed(2)}
                        </span>
                    </li>
                ))}
            </ul>

            <div className="mt-3 pt-3 border-t border-mint flex flex-wrap items-end justify-between gap-3">
                <div className="text-xs text-muted2 space-y-0.5">
                    <p className="flex items-center gap-1.5">
                        <Calendar size={12} className="text-forest" />
                        Deliver:{" "}
                        <span className="text-navy font-bold">
                            {order.delivery_date}
                        </span>
                    </p>
                    <p className="flex items-center gap-1.5 max-w-md truncate">
                        <MapPin size={12} className="text-forest" />
                        {order.customer?.address}
                    </p>
                    <p className="flex items-center gap-1.5">
                        <Phone size={12} className="text-forest" />
                        {order.customer?.phone}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <span className="font-display text-3xl text-forest tabular-nums">
                        ${order.total.toFixed(2)}
                    </span>
                    <Button
                        data-testid={`reorder-btn-${order.order_id}`}
                        onClick={onReorder}
                        className="rounded-full bg-gold hover:bg-goldDark text-navy font-bold py-2 h-auto px-4 text-sm flex items-center gap-1.5"
                    >
                        <RotateCcw size={14} /> Re-order
                    </Button>
                </div>
            </div>
        </li>
    );
}
